import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Copy, CheckCircle2, Loader2, AlertCircle, Code, Server, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { productAPI } from '../../services/api';
import { isValidProductId, resolveProductId } from '@/lib/productId';
import {
  TailAdminCodeBlock,
  TailAdminPageHeader,
  TailAdminPanel,
} from '@/components/template/tailadmin/DashboardPrimitives';

const codeExamples = {
  nodejs: `// Node.js / Express
const axios = require('axios');

app.post('/api/signup', async (req, res) => {
  const { phone, email, ref_token } = req.body;

  // 1. Create user in your database
  const user = await db.createUser({...});

  // 2. Notify Proovam (if ref_token exists)
  if (ref_token) {
    await axios.post(
      'https://proovam.com/api/referral/track/conversion',
      {
        ref_token: ref_token,
        product_id: 'PRODUCT_ID',
        user_data: { phone, email }
      },
      {
        headers: {
          'Authorization': 'Bearer YOUR_API_KEY',
          'Content-Type': 'application/json'
        }
      }
    );
  }

  res.json({ success: true });
});`,
  
  python: `# Python / FastAPI
import httpx

@app.post("/api/signup")
async def signup(data: SignupRequest):
    # 1. Create user in your database
    user = await db.create_user(...)
    
    # 2. Notify Proovam (if ref_token exists)
    if data.ref_token:
        async with httpx.AsyncClient() as client:
            await client.post(
                "https://proovam.com/api/referral/track/conversion",
                json={
                    "ref_token": data.ref_token,
                    "product_id": "PRODUCT_ID",
                    "user_data": {
                        "phone": data.phone,
                        "email": data.email
                    }
                },
                headers={
                    "Authorization": f"Bearer YOUR_API_KEY",
                    "Content-Type": "application/json"
                }
            )
    
    return {"success": True}`,

  php: `<?php
// PHP
$refToken = $_POST['ref_token'] ?? null;
$phone = $_POST['phone'];
$email = $_POST['email'];

// 1. Create user in your database
$user = createUser($phone, $email);

// 2. Notify Proovam (if ref_token exists)
if ($refToken) {
    $ch = curl_init('https://proovam.com/api/referral/track/conversion');
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'Authorization: Bearer YOUR_API_KEY',
        'Content-Type: application/json'
    ]);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
        'ref_token' => $refToken,
        'product_id' => 'PRODUCT_ID',
        'user_data' => [
            'phone' => $phone,
            'email' => $email
        ]
    ]));
    curl_exec($ch);
    curl_close($ch);
}
?>`
};

const ProveRequestSetup = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [integrationMethod, setIntegrationMethod] = useState('js_tracker');

  const loadProduct = useCallback(async () => {
    if (!isValidProductId(productId)) {
      toast.error('Invalid product — select an offer first');
      navigate('/client/products', { replace: true });
      return;
    }

    setLoading(true);
    try {
      const response = await productAPI.getProduct(productId);
      const nextProduct = response.data.product || response.data;
      setProduct(nextProduct);
      
      // Set integration method based on product type
      if (nextProduct.productType === 'mobile_app') {
        setIntegrationMethod('webhook');
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Failed to load product');
      navigate('/client/products', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleValidate = async () => {
    if (!isValidProductId(productId)) {
      toast.error('Invalid product');
      navigate('/client/products', { replace: true });
      return;
    }

    setValidating(true);
    setValidationStatus('pending');

    try {
      // Call validation endpoint (to be implemented)
      // const response = await productAPI.validateIntegration(productId);
      
      // For now, simulate validation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Check if test conversion was received
      // if (response.data.validated) {
      //   setValidationStatus('success');
      // } else {
      //   setValidationStatus('pending');
      // }
      
      // Simulated success
      setValidationStatus('success');
      toast.success('Integration validated successfully!');
      
      // Navigate back to products after 2 seconds
      setTimeout(() => {
        navigate('/client/products');
      }, 2000);
      
    } catch (error) {
      console.error('Validation error:', error);
      setValidationStatus('error');
      toast.error('Validation failed. Please check your integration.');
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Product not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const jsTrackerCode = `<script src="https://cdn.proovam.com/tracker.js"></script>
<script>
  ProovamTracker.init({
    productId: '${resolveProductId(product)}',
    conversionEndpoint: '/api/proovam/conversion'
  });

  // On form submit:
  document.getElementById('signupForm').addEventListener('submit', function(e) {
    e.preventDefault();
    
    ProovamTracker.trackConversion({
      ${product.userIdentifierType}: document.getElementById('${product.userIdentifierType}').value,
      email: document.getElementById('email').value,
      name: document.getElementById('name').value
    }).then(result => {
      if (result.success) {
        // Continue with your normal signup flow
        console.log('Attribution tracked!');
      }
    });
  });
</script>`;

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
      <TailAdminPageHeader
        eyebrow="Referral setup"
        title="Integration Setup"
        description={`Set up referral tracking for ${product.name}.`}
        actions={
          <Button variant="outline" onClick={() => navigate('/client/products')}>
            Back to Prove-Requests
          </Button>
        }
      />

      {/* Product Type Badge */}
      <TailAdminPanel title="Product Type" description="Use the integration path that matches how users sign up." action={null}>
          <div className="flex items-center gap-4 rounded-xl border border-gray-100 p-4">
            {product.productType === 'mobile_app' ? (
              <Smartphone className="w-8 h-8 text-brand-500" />
            ) : (
              <Code className="w-8 h-8 text-brand-500" />
            )}
            <div>
              <p className="font-semibold text-gray-900">
                {product.productType === 'mobile_app' ? 'Native Mobile App' : 'Web Application'}
              </p>
              <p className="text-sm text-gray-500">
                {product.productType === 'mobile_app' 
                  ? 'Webhook integration required' 
                  : 'Choose between JS tracker (recommended) or webhook'}
              </p>
            </div>
          </div>
      </TailAdminPanel>

      {/* Integration Methods */}
      <Tabs value={integrationMethod} onValueChange={setIntegrationMethod}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="js_tracker" disabled={product.productType === 'mobile_app'}>
            <Code className="w-4 h-4 mr-2" />
            JS Tracker {product.productType !== 'mobile_app' && '(Recommended)'}
          </TabsTrigger>
          <TabsTrigger value="webhook">
            <Server className="w-4 h-4 mr-2" />
            Server-Side Webhook
          </TabsTrigger>
        </TabsList>

        {/* JS Tracker Tab */}
        <TabsContent value="js_tracker" className="space-y-4">
          <TailAdminPanel
            title="JavaScript Tracker Integration"
            description="Add this code to your signup or registration page."
            action={<Code className="h-5 w-5 text-brand-500" />}
          >
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-medium">Step 1: Add Tracker Script</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(jsTrackerCode)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                </div>
                <TailAdminCodeBlock>{jsTrackerCode}</TailAdminCodeBlock>
              </div>

              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <strong>Benefits:</strong> Easiest setup, automatic tracking, works across all pages.
                </AlertDescription>
              </Alert>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Note:</strong> Some users may have script blockers. For full coverage, also implement the webhook method.
                </AlertDescription>
              </Alert>
            </div>
          </TailAdminPanel>
        </TabsContent>

        {/* Webhook Tab */}
        <TabsContent value="webhook" className="space-y-4">
          <TailAdminPanel
            title="Server-Side Webhook Integration"
            description="Add this to your backend when users sign up."
            action={<Server className="h-5 w-5 text-brand-500" />}
          >
            <div className="space-y-4">
              <Tabs defaultValue="nodejs">
                <TabsList>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                  <TabsTrigger value="php">PHP</TabsTrigger>
                </TabsList>

                {Object.keys(codeExamples).map(lang => (
                  <TabsContent key={lang} value={lang}>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-medium capitalize">{lang} Example</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(
                            codeExamples[lang]
                              .replace('PRODUCT_ID', resolveProductId(product))
                              .replace('YOUR_API_KEY', product.apiKey || product.api_key || '')
                          )}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </Button>
                      </div>
                      <TailAdminCodeBlock>
                        {codeExamples[lang]
                          .replace('PRODUCT_ID', resolveProductId(product))
                          .replace('YOUR_API_KEY', product.apiKey || product.api_key || '')}
                      </TailAdminCodeBlock>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <strong>Benefits:</strong> More reliable backend-to-backend tracking, script-blocker resistant, and secure.
                </AlertDescription>
              </Alert>
            </div>
          </TailAdminPanel>
        </TabsContent>
      </Tabs>

      {/* Validation Section */}
      <TailAdminPanel
        title="Test Your Integration"
        description="Verify that tracking is working correctly before enabling referral promotion."
        action={<CheckCircle2 className="h-5 w-5 text-brand-500" />}
      >
        <div className="space-y-4">
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Test Steps</h4>
            <ol className="space-y-2 text-sm leading-6 text-gray-600">
              <li><strong>1.</strong> Add the integration code to your {product.productType === 'mobile_app' ? 'app' : 'website'}</li>
              <li><strong>2.</strong> Complete a test signup with test data:
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 font-mono text-xs text-gray-700">
                  {product.userIdentifierType === 'phone' && 'Phone: 08099999999'}
                  {product.userIdentifierType === 'email' && 'Email: test@proovam.com'}
                  {product.userIdentifierType === 'custom' && 'Custom ID: test-user-001'}
                </div>
              </li>
              <li><strong>3.</strong> Click "Validate Integration" below</li>
            </ol>
          </div>

          <div className="flex items-center space-x-4">
            <Button
              onClick={handleValidate}
              disabled={validating || validationStatus === 'success'}
              className="flex-1"
            >
              {validating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {validationStatus === 'success' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Integration Verified!
                </>
              ) : (
                'Validate Integration'
              )}
            </Button>
          </div>

          {validationStatus === 'pending' && !validating && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                No conversion event received yet. Please complete the test signup and try again.
              </AlertDescription>
            </Alert>
          )}

          {validationStatus === 'error' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Integration not detected. Please check your code and try again.
                <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/docs/troubleshooting')}>
                  View Troubleshooting Guide
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {validationStatus === 'success' && (
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                <strong>Success!</strong> Referral promotion is now active. Your BRMs can start sharing links.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </TailAdminPanel>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => navigate('/client/products')}
        >
          {validationStatus === 'success' ? 'Go to Prove-Requests' : 'Skip for Now'}
        </Button>

        {validationStatus !== 'success' && (
          <Button variant="link" onClick={() => navigate('/docs/integration')}>
            View Full Documentation
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProveRequestSetup;
